import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { eraseCookie, getCookie, setCookie } from "../utils/cookies";
import AuthService from "../api/services/auth.service";

interface LoginCredentials {
  email: string;
  password: string;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  profile_photo?: string;
  preferred_language: string;
  role: string;
  created: string;
}

interface AuthContextType {
  email: string;
  setEmail: (email: string) => void;
  isLoggedIn: boolean;
  setIsLoggedIn: (value: boolean) => void;
  user: User | null;
  setUser: (user: User | null) => void;
  checkAuth: () => Promise<boolean>;
  logout: () => Promise<void>;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  email: "",
  setEmail: () => {},
  isLoggedIn: false,
  setIsLoggedIn: () => {},
  user: null,
  setUser: () => {},
  checkAuth: async () => false,
  logout: async () => {},
  loading: true,
  login: async () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [email, setEmail] = useState<string>("");
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<void> => {
      try {
        // LoginCredentials'i FormData'ya çevir
        const formData = new FormData();
        formData.append("email", credentials.email);
        formData.append("password", credentials.password);

        const response = await AuthService.login(formData);

        if (response.data.access_token) {
          setCookie("LuxMartClientAccessToken", response.data.access_token, 7);
          setEmail(credentials.email);
          setIsLoggedIn(true);

          if (response.data.user) {
            setUser(response.data.user);
          } else {
            try {
              const userInfo = await AuthService.getUserInfo();
              if (userInfo.data) {
                setUser(userInfo.data);
              }
            } catch (userError) {
              console.warn("Could not fetch user info:", userError);
            }
          }

          return;
        }
        throw new Error("No access token received");
      } catch (error) {
        throw error;
      }
    },
    [],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      const token = getCookie("LuxMartClientAccessToken");

      if (token) {
        try {
          await AuthService.logout();
        } catch (apiError: any) {
          if (apiError.response?.status !== 401) {
            console.warn("Logout API error:", apiError.message);
          }
        }
      }
    } finally {
      eraseCookie("LuxMartClientAccessToken");
      localStorage.removeItem("guest_basket");
      setEmail("");
      setIsLoggedIn(false);
      setUser(null);

      const currentPath = window.location.pathname;
      let language = "az";

      const languageMatch = currentPath.match(/^\/(az|en|ru)(\/|$)/);
      if (languageMatch) {
        language = languageMatch[1];
      } else {
        const savedLanguage = localStorage.getItem("i18nextLng");
        if (
          savedLanguage &&
          (savedLanguage === "az" ||
            savedLanguage === "en" ||
            savedLanguage === "ru")
        ) {
          language = savedLanguage;
        }
      }

      window.location.href = `/${language}/auth/login`;
    }
  }, []);

  const checkAuth = useCallback(async (): Promise<boolean> => {
    const accessToken = getCookie("LuxMartClientAccessToken");

    if (!accessToken) {
      setIsLoggedIn(false);
      setUser(null);
      return false;
    }

    try {
      const userInfo = await AuthService.getUserInfo();

      if (userInfo.data?.email) {
        setEmail(userInfo.data.email);
        setIsLoggedIn(true);

        if (userInfo.data) {
          const userData: User = {
            id: userInfo.data.id,
            first_name: userInfo.data.first_name || "",
            last_name: userInfo.data.last_name || "",
            email: userInfo.data.email,
            phone: userInfo.data.phone,
            profile_photo: userInfo.data.profile_photo,
            preferred_language:
              userInfo.data.preferred_language || "AZERBAIJANI",
            role: userInfo.data.role || "user",
            created: userInfo.data.created,
          };
          setUser(userData);
        }

        return true;
      } else {
        await logout();
        return false;
      }
    } catch (error: any) {
      console.error("Auth check failed:", error);

      if (error.response?.status === 401) {
        await logout();
      }
      return false;
    }
  }, [logout]);

  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      await checkAuth();
      setLoading(false);
    };

    initAuth();
  }, [checkAuth]);

  const value: AuthContextType = {
    email,
    setEmail,
    isLoggedIn,
    setIsLoggedIn,
    user,
    setUser,
    checkAuth,
    logout,
    loading,
    login,
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "var(--bg-f8f9fa)",
        }}
      >
        <div
          style={{
            fontSize: "16px",
            color: "var(--fg-64748b)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div
            className="spinner-border spinner-border-sm text-primary"
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <span>Loading authentication...</span>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
